import { useCallback, useMemo } from "react";

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { formatKg, getNiceYAxis } from "../../utils/formatting";
import type { WasteAmountsPoint, WasteAmountsStream } from "../types";
import { WasteTooltip } from "./tooltip";

type Props = {
  data: WasteAmountsPoint[];
  wasteStreams: WasteAmountsStream[];
};

export const WasteAmountsGraph: React.FC<Props> = ({ data, wasteStreams }) => {
  const lblStep = useMemo(
    () => Math.max(1, Math.round(data.length / 12)),
    [data],
  );

  const { niceMax, ticks } = useMemo(
    () => getNiceYAxis(Math.max(...data.map((d) => d.total))),
    [data],
  );

  const renderTotalLabel = useCallback(
    (props: any) => {
      const { x, y, value, index } = props;
      if (index % lblStep !== 0 && index !== data.length - 1) {
        return null;
      }

      return (
        <text
          key={`total-${index}`}
          x={x}
          y={y - 4.95}
          textAnchor="middle"
          fontSize={10}
          fontWeight={700}
          fill="#1D1C1C"
        >
          {(value / 1000).toFixed(1)}k
        </text>
      );
    },
    [data.length, lblStep],
  );

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={data}
        margin={{ top: 21.45, right: 8.25, left: 0, bottom: 0 }}
        barCategoryGap="24%"
      >
        <CartesianGrid stroke="#EEE7E7" vertical={false} />
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 10, fill: "#6F6C6C" }}
        />
        <YAxis
          domain={[0, niceMax]}
          ticks={ticks}
          tickFormatter={formatKg}
          tickLine={false}
          axisLine={false}
          width={30.8}
          tick={{ fontSize: 10.5, fill: "#B2ABAB" }}
        />
        <Tooltip
          content={<WasteTooltip streams={wasteStreams} />}
          cursor={{ fill: "rgba(0,0,0,0.04)" }}
        />

        {wasteStreams.map((s, i) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.name}
            stackId="waste"
            fill={s.color}
            radius={i === wasteStreams.length - 1 ? [2.475, 2.475, 0, 0] : 0}
          />
        ))}

        <Line
          dataKey="total"
          stroke="none"
          dot={false}
          activeDot={false}
          isAnimationActive={false}
          label={renderTotalLabel}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};
